import Popup, { type PopupProps } from '@CS/components/Popups/Popup';
import { pushEvent } from '@CS/hooks';
import ScoreBar from '@/components/ScoreBar/ScoreBar';
import BaseButton from '@/components/BaseButton/BaseButton';
import { useRef, useState, type AnimationEventHandler } from 'react';
import CloseIcon from '@CS/assets/close.svg?react';

import styles from './GameMenuPopup.module.css';
import { openHome } from '@/services/app/mcd';

type GameMenuPopupProps = PopupProps & {
  trendingPoints: number;
  teletekstPoints: number;
  translations: Record<string, string>;
  onResume: () => void;
  onOpenRules: () => void;
};

const GameMenuPopup = ({
  trendingPoints,
  teletekstPoints,
  translations,
  onClose,
  onResume,
  onOpenRules,
  ...props
}: GameMenuPopupProps) => {
  const title = translations.gameMenuTitle ?? 'MENU';
  const scoreLabel = translations.gameMenuScoreLabel ?? 'TUSSENSTAND';
  const endGameLabel = translations.gameMenuEndGame ?? 'SPEL AFSLUITEN';
  const rulesLabel = translations.gameMenuRules ?? 'SPELREGELS';
  const resumeLabel = translations.gameMenuResume ?? 'GAME HERVATTEN';
  const [isClosing, setIsClosing] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const pendingCloseActionRef = useRef<(() => void) | null>(null);

  const handleClose = () => {
    setIsClosing(true);
    pendingCloseActionRef.current = onClose ?? null;
  };

  const withCloseAnimation = (action: () => void) => {
    setIsClosing(true);
    pendingCloseActionRef.current = action;
  };

  const handleResume = () => {
    pushEvent({
      event: 'button_click',
      eventAction: '/fcg26/game_hervatten',
      eventLabel: '/fcg26/menu'
    });
    withCloseAnimation(onResume);
  };

  const handleOpenRules = () => {
    pushEvent({
      event: 'button_click',
      eventAction: '/fcg26/spelregels',
      eventLabel: '/fcg26/menu'
    });
    withCloseAnimation(() => {
      onClose?.();
      onOpenRules();
    });
  };

  const handleRollAnimationEnd: AnimationEventHandler<HTMLDivElement> = (event) => {
    if (!isClosing) return;
    if (event.target !== event.currentTarget) return;
    if (event.animationName !== 'popup-roll-out-right') return;

    const action = pendingCloseActionRef.current;
    pendingCloseActionRef.current = null;
    action?.();
  };

  return (
    <Popup
      onClose={onClose}
      onBeforeClose={() => setIsClosing(true)}
      hideClose
      allowClickOutside
      className={styles.dialog}
      width="100%"
      opacity={0.6}
      customStyle={{
        backgroundColor: 'transparent',
        height: '100%',
        paddingTop: '5.6rem'
      }}
      {...props}
    >
      <div
        ref={contentRef}
        onAnimationEnd={handleRollAnimationEnd}
        className={[
          styles.popupContent,
          'popup-roll',
          isClosing && 'popup-roll--closing'
        ].filter(Boolean).join(' ')}
      >
        <button
          type="button"
          className={styles.closeButton}
          aria-label="Close"
          onClick={handleClose}
        >
          <CloseIcon />
        </button>
        <div className={styles.body}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{scoreLabel}</p>

          <div className={styles.scoreWrap}>
            <ScoreBar
              trending={trendingPoints}
              teletekst={teletekstPoints}
              questionIndex={0}
              trendingColor="var(--teamTrending)"
              teletekstColor="var(--teamTeletekst)"
              trendingStrokeColor="#0183B2"
              teletekstStrokeColor="#A041AB"
              variant={window.innerWidth <= 380 && window.innerHeight <= 700 ? "popupSmall" : "popup"}
            />
          </div>
        </div>

        <div className={styles.actions}>
          <BaseButton
            text={endGameLabel}
            className={styles.actionButton}
            onClick={() => {
              openHome();
              pushEvent({
                event: 'button_click',
                eventAction: '/fcg26/spel_afsluiten',
                eventLabel: '/fcg26/menu'
              });
            }}
          />
          <BaseButton
            text={rulesLabel}
            className={styles.actionButton}
            onClick={handleOpenRules}
          />
          <BaseButton
            text={resumeLabel}
            className={`${styles.actionButton} ${styles.resumeButton}`}
            onClick={handleResume}
          />
        </div>
      </div>
    </Popup>
  );
};

export default GameMenuPopup;
